import { AuthConfig } from '@dustin-web/auth-js/dist/types/types'
import { getEnvironmentUrl } from './site-context'

export type ServerEnvironment = 'local' | 'test' | 'staging' | 'production'
export type ServerSettingsEnvironment =
  | ServerEnvironment
  | 'aws_local'
  | 'aws_test'
  | 'aws_staging'
  | 'aws_production'

export type Settings = typeof defaults
type Params = { [key: string]: string }

enum LanguageForResource {
  Sv = 'sv',
  En = 'en',
  Nb = 'nb',
  Da = 'da',
  Fi = 'fi',
  Nl = 'nl',
  Fr = 'fr',
}

const {
  ASPNETCORE_ENVIRONMENT,
  AWS_ENVIRONMENT,
  PRODUCT_CATALOG_UPDATER,
  CATEGORY_QUERY,
  GRAPHQL_BROWSER,
  GRAPHQL_SERVER = 'http://gateway-graphql',
  GRAPHQL_PATH = '/graphql',
  RESOURCES_QUERY,
  TOGGELITO_QUERY,
  SHOW_HEADER,
  I18N_NS,
  SITE_NAME,
  OPTIMIZELY_SDK_KEY,
} = process ? process.env : ({} as Params)

const serverEnv = (ASPNETCORE_ENVIRONMENT?.toLowerCase() || 'local') as ServerEnvironment
const serverSettingsEnv = (AWS_ENVIRONMENT?.toLowerCase() ||
  ASPNETCORE_ENVIRONMENT?.toLowerCase() ||
  'local') as ServerSettingsEnvironment

const graphqlAppUris = {
  graphqlBrowser: GRAPHQL_BROWSER || getEnvironmentUrl(GRAPHQL_SERVER, serverEnv, true),
  graphqlServer: GRAPHQL_SERVER,
  graphqlPath: GRAPHQL_PATH,
}

const authConfig: AuthConfig = {
  siteType: 'Business',
  sourcingCompany: 'SE',
  oktaOpenIdConfigurationUrl:
    'https://login.test.dustin.eu/oauth2/default/.well-known/openid-configuration?client_id=0oascbbk8Cyxzl6IL0x6',
  oktaOpenIdIssuer: 'https://login.test.dustin.eu/oauth2/default',
  azureAdOpenIdConfigurationUrl:
    'https://login.microsoftonline.com/e50f2ec7-cfc1-43f2-9aef-956fdbd5b860/v2.0/.well-known/openid-configuration',
  azureAdOpenIdIssuer:
    'https://login.microsoftonline.com/e50f2ec7-cfc1-43f2-9aef-956fdbd5b860/v2.0',
  dustinOpenIdIssuer: 'Dustin',
  authServerUrl: '',
  dustinOpenIdConfigurationUrl: '',
}

// Dustin FX project: https://app.optimizely.com/v2/projects/6416802054406144/flags/list
const optimizelySdkKeys: Record<ServerEnvironment, string> = {
  local: '6ZKtPDRfvAWnTfHa8L8Wz',
  test: '6ZKtPDRfvAWnTfHa8L8Wz',
  staging: '5cDdT2rzyVYWAHHCWm6Gq',
  production: 'UMaqXuEMVmpsJdEZ4rc9c',
}

const getResourceQueryUrl = () => {
  if (serverEnv === 'production') {
    return 'http://resources-query'
  }

  return (
    RESOURCES_QUERY ||
    getEnvironmentUrl(
      'http://resources-query',
      serverSettingsEnv === 'aws_production' ? 'aws_production' : 'production'
    )
  )
}

const defaults = {
  serverEnv,
  serverSettingsEnv,
  siteName: SITE_NAME || 'microsite',
  showHeader: SHOW_HEADER || 'true',
  productUseCaseService:
    PRODUCT_CATALOG_UPDATER ||
    getEnvironmentUrl('http://product-catalog-updater', serverSettingsEnv),
  categoryQuery: CATEGORY_QUERY || getEnvironmentUrl('http://category-query', serverSettingsEnv),
  toggelitoQuery: TOGGELITO_QUERY || getEnvironmentUrl('http://toggelito-query', serverSettingsEnv),
  i18n: {
    languages: Object.keys(LanguageForResource).map(lng => lng.toLowerCase()),
    ns: (I18N_NS || 'BaseLayout').split(','),
    resourcesQuery: getResourceQueryUrl(),
  },
  authConfig,
  authServerConfig: { ...authConfig },
  optimizelySdkKey: OPTIMIZELY_SDK_KEY || optimizelySdkKeys[serverEnv],
  ...graphqlAppUris,
}

const settings = {
  local: {
    ...defaults,
    authConfig: {
      ...authConfig,
      debug: false,
      authServerUrl: 'https://auth.test.dustin.services/auth',
      dustinOpenIdConfigurationUrl:
        'https://auth.test.dustin.services/auth/.well-known/openid-configuration',
    },
    authServerConfig: {
      ...authConfig,
      debug: false,
      authServerUrl: 'https://auth.test.dustin.services/auth',
      dustinOpenIdConfigurationUrl:
        'https://auth.test.dustin.services/auth/.well-known/openid-configuration',
    },
  },
  aws_local: {
    ...defaults,
    authConfig: {
      ...authConfig,
      debug: false,
      authServerUrl: 'https://auth.test.cnt.dustintech.dev/auth',
      dustinOpenIdConfigurationUrl:
        'https://auth.test.cnt.dustintech.dev/auth/.well-known/openid-configuration',
    },
    authServerConfig: {
      ...authConfig,
      debug: false,
      authServerUrl: 'https://auth.test.cnt.dustintech.dev/auth',
      dustinOpenIdConfigurationUrl:
        'https://auth.test.cnt.dustintech.dev/auth/.well-known/openid-configuration',
    },
  },
  test: {
    ...defaults,
    authConfig: {
      ...authConfig,
      authServerUrl: 'https://test.dustin.se/auth',
      dustinOpenIdConfigurationUrl: 'https://test.dustin.se/auth/.well-known/openid-configuration',
      debug: true,
    },
    authServerConfig: {
      ...authConfig,
      authServerUrl: 'http://auth/auth',
      dustinOpenIdConfigurationUrl: 'http://auth/auth/.well-known/openid-configuration',
      debug: true,
    },
  },
  aws_test: {
    ...defaults,
    authConfig: {
      ...authConfig,
      authServerUrl: 'https://dustin-se.test.cnt.dustintech.dev/auth',
      dustinOpenIdConfigurationUrl:
        'https://dustin-se.test.cnt.dustintech.dev/auth/.well-known/openid-configuration',
      debug: true,
    },
    authServerConfig: {
      ...authConfig,
      authServerUrl: 'http://auth/auth',
      dustinOpenIdConfigurationUrl: 'http://auth/auth/.well-known/openid-configuration',
      debug: true,
    },
  },
  staging: {
    ...defaults,
    authConfig: {
      ...authConfig,
      authServerUrl: 'https://stage.dustin.se/auth',
      dustinOpenIdConfigurationUrl: 'https://stage.dustin.se/auth/.well-known/openid-configuration',
      oktaOpenIdConfigurationUrl:
        'https://login.stage.dustin.eu/oauth2/default/.well-known/openid-configuration?client_id=0oa1jm4qkhO7E2m3r0x7',
      oktaOpenIdIssuer: 'https://login.stage.dustin.eu/oauth2/default',
    },
    authServerConfig: {
      ...authConfig,
      authServerUrl: 'http://auth/auth',
      dustinOpenIdConfigurationUrl: 'http://auth/auth/.well-known/openid-configuration',
      oktaOpenIdConfigurationUrl:
        'https://login.stage.dustin.eu/oauth2/default/.well-known/openid-configuration?client_id=0oa1jm4qkhO7E2m3r0x7',
      oktaOpenIdIssuer: 'https://login.stage.dustin.eu/oauth2/default',
    },
  },
  aws_staging: {
    ...defaults,
    authConfig: {
      ...authConfig,
      authServerUrl: 'https://dustin-se.acc.cnt.dustintech.dev/auth',
      dustinOpenIdConfigurationUrl:
        'https://dustin-se.acc.cnt.dustintech.dev/auth/.well-known/openid-configuration',
      oktaOpenIdConfigurationUrl:
        'https://login.stage.dustin.eu/oauth2/default/.well-known/openid-configuration?client_id=0oa1jm4qkhO7E2m3r0x7',
      oktaOpenIdIssuer: 'https://login.stage.dustin.eu/oauth2/default',
    },
    authServerConfig: {
      ...authConfig,
      authServerUrl: 'http://auth/auth',
      dustinOpenIdConfigurationUrl: 'http://auth/auth/.well-known/openid-configuration',
      oktaOpenIdConfigurationUrl:
        'https://login.stage.dustin.eu/oauth2/default/.well-known/openid-configuration?client_id=0oa1jm4qkhO7E2m3r0x7',
      oktaOpenIdIssuer: 'https://login.stage.dustin.eu/oauth2/default',
    },
  },
  production: {
    ...defaults,
    i18n: {
      ...defaults.i18n,
      resourcesQuery: RESOURCES_QUERY || 'http://resources-query',
    },
    authConfig: {
      ...authConfig,
      authServerUrl: 'https://www.dustin.se/auth',
      dustinOpenIdConfigurationUrl: 'https://www.dustin.se/auth/.well-known/openid-configuration',
      oktaOpenIdConfigurationUrl:
        'https://login.dustin.eu/oauth2/default/.well-known/openid-configuration?client_id=0oasxsl6bKpAMRnMj416',
      oktaOpenIdIssuer: 'https://login.dustin.eu/oauth2/default',
    },
    authServerConfig: {
      ...authConfig,
      authServerUrl: 'http://auth/auth',
      dustinOpenIdConfigurationUrl: 'http://auth/auth/.well-known/openid-configuration',
      oktaOpenIdConfigurationUrl:
        'https://login.dustin.eu/oauth2/default/.well-known/openid-configuration?client_id=0oasxsl6bKpAMRnMj416',
      oktaOpenIdIssuer: 'https://login.dustin.eu/oauth2/default',
    },
  },
  aws_production: {
    ...defaults,
    i18n: {
      ...defaults.i18n,
      resourcesQuery: RESOURCES_QUERY || 'http://resources-query',
    },
    authConfig: {
      ...authConfig,
      authServerUrl: 'https://dustin-se.cnt.dustintech.io/auth',
      dustinOpenIdConfigurationUrl:
        'https://dustin-se.cnt.dustintech.io/auth/.well-known/openid-configuration',
      oktaOpenIdConfigurationUrl:
        'https://login.dustin.eu/oauth2/default/.well-known/openid-configuration?client_id=0oasxsl6bKpAMRnMj416',
      oktaOpenIdIssuer: 'https://login.dustin.eu/oauth2/default',
    },
    authServerConfig: {
      ...authConfig,
      authServerUrl: 'http://auth/auth',
      dustinOpenIdConfigurationUrl: 'http://auth/auth/.well-known/openid-configuration',
      oktaOpenIdConfigurationUrl:
        'https://login.dustin.eu/oauth2/default/.well-known/openid-configuration?client_id=0oasxsl6bKpAMRnMj416',
      oktaOpenIdIssuer: 'https://login.dustin.eu/oauth2/default',
    },
  },
}
export default settings[serverSettingsEnv] as Settings
